import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import {first} from "lodash";
import moment from "moment";
import {AnimalTypes} from "../constans/animalTypes";
import animalsDB from "../database/animalsDB";
import groupsDB from "../database/groupsDB";
import {isBetween} from "./MathUtils";
import GroupMortalityQuery from "./queries/GroupMortality.query";
import {convertWeightUnitTo} from "./UnitUtils";
import {getTransferGroupData} from "./TransferGroupUtils";
import {UnitTypes} from "../constans/unitTypes";
import SectorTypes from "@wesstron/utils/Api/constants/sectorTypes";
import Flatbush from "flatbush";

export class EstimatedWeightIndex {
    constructor(estimatedWeight) {
        this.entries = Object.entries(estimatedWeight)
            .map(([t, w]) => [Number(t), w])
            .filter(([t]) => Number.isFinite(t))
            .sort((a, b) => a[0] - b[0]);

        this.index = null;

        if (this.entries.length > 0) {
            this.index = new Flatbush(this.entries.length);
            for (const [t] of this.entries) {
                this.index.add(t, 0, t, 0);
            }
            this.index.finish();
        }
    }

    getClosest(evTime) {
        if (!this.index) return null;

        const ids = this.index.neighbors(evTime, 0, 1);
        if (!ids.length) return null;

        return this.entries[ids[0]][1];
    }
}

export const GROUP_ANIMAL_TYPES = `SELECT 
  SUM(
    CASE WHEN key = 'weanedInsertedCnt' THEN value :: INTEGER END
  ) > 0 as hasWeaners, 
  SUM(
    CASE WHEN key = 'giltInsertedCnt' THEN value :: INTEGER END
  ) > 0 as hasGilts, 
  SUM(
    CASE WHEN key = 'finisherInsertedCnt' 
    OR key = 'pigletReclassifyToPorker' THEN value :: INTEGER END
  ) > 0 as hasFinisher 
FROM 
  ParamsTable 
WHERE 
  group_nr = ? 
  AND key IN (
    'weanedInsertedCnt', 'giltInsertedCnt', 
    'finisherInsertedCnt', 'pigletReclassifyToPorker'
  )
`;

export const GROUP_FATTENING = `SELECT 
  TRY_CAST(
    date_diff(
      'day', 
      FIRST(
        try_cast(value AS DATE)
      ) FILTER(
        WHERE 
          key = 'weanedPigPigletHouseStart'
      ), 
      COALESCE(
        FIRST(
          try_cast(value AS DATE)
        ) FILTER(
          WHERE 
            key = 'weanedPigPigletHouseEnd'
        ), 
        today()
      )
    ) AS INTEGER
  ) as weanersAge, 
  TRY_CAST(
    date_diff(
      'day', 
      FIRST(
        try_cast(value AS DATE)
      ) FILTER(
        WHERE 
          key = 'finisherPigPorkHouseStart'
      ), 
      COALESCE(
        FIRST(
          try_cast(value AS DATE)
        ) FILTER(
          WHERE 
            key = 'finisherPigPorkHouseEnd'
        ), 
        today()
      )
    ) AS INTEGER
  ) as finisherAge 
FROM 
  ParamsTable 
WHERE 
  group_nr = ? 
  AND key IN (
    'weanedPigPigletHouseStart', 'weanedPigPigletHouseEnd', 
    'finisherPigPorkHouseStart', 'finisherPigPorkHouseEnd'
  )
`;

function groupsSortedByInsertion(yearRange) {
    return `
        SELECT group_nr, MIN(dataEv) as dataEv
        FROM
            ParamsTable
        WHERE key IN (
            'finisherInsertedCnt'
            , 'giltInsertedCnt'
            , 'weanedInsertedCnt'
            )
          AND dataEv
            > ${yearRange[0]}
          AND dataEv
            < ${yearRange[1]}
          AND group_nr IS NOT NULL
          AND NOT REGEXP_FULL_MATCH(group_nr
            , '\\S*\\s\\(\\d*\\)')
          AND group_nr <> 'UNKNOWN'
        GROUP BY
            group_nr
        ORDER BY
            dataEv ASC, group_nr ASC
    `;
}

function createLastXInserted(limit) {
    return `
        SELECT group_nr, MAX(dataEv) as dataEv
        FROM
            ParamsTable
        WHERE
            key IN (
            'finisherInsertedCnt'
            , 'giltInsertedCnt'
            , 'weanedInsertedCnt'
            )
          AND group_nr IS NOT NULL
          AND NOT REGEXP_FULL_MATCH(group_nr
            , '\\S*\\s\\(\\d*\\)')
          AND group_nr <> 'UNKNOWN'
        GROUP BY
            group_nr
        ORDER BY
            dataEv DESC, group_nr ASC
            LIMIT ${limit}
    `;
}

function createLastXClosed(limit) {
    return `
        SELECT group_nr, MAX(value) as lastValue
        FROM
            ParamsTable
        WHERE
            key = 'groupCloseTime'
          AND group_nr IS NOT NULL
          AND NOT REGEXP_FULL_MATCH(group_nr
            , '\\S*\\s\\(\\d*\\)')
          AND group_nr <> 'UNKNOWN'
        GROUP BY
            group_nr
        ORDER BY
            lastValue DESC, group_nr ASC
            LIMIT ${limit}
    `;
}

function getGroupForAnimals(animals) {
    for (let animal of animals) {
        const groups = groupsDB.getGroupWithAnimal(animal.AnmID, animal.FarmID);
        if (groups.length > 0) return groups[0];
    }
    return null;
}

export function checkGroupInsertionTimeRange(
    group,
    InsertionTime,
    maxDaysRange = 0
) {
    if (maxDaysRange === 0) return true; // wylaczona walidacja
    let minTime = null;
    let maxTime = null;
    if (Array.isArray(group.AnmIDs)) {
        for (let AnmID of group.AnmIDs) {
            const animal = animalsDB.getAnimalById(AnmID, {joinEvents: false});
            if (animal?.DtaInTime) {
                if (minTime === null || animal.DtaInTime < minTime) {
                    minTime = animal.DtaInTime;
                }
                if (maxTime === null || animal.DtaInTime > maxTime) {
                    maxTime = animal.DtaInTime;
                }
            }
        }
    }
    if (minTime === null) minTime = group.DtaModTime;
    if (maxTime === null) maxTime = group.DtaModTime;
    const _minTime = +moment(maxTime)
        .startOf("day")
        .subtract(maxDaysRange, "days");
    const _maxTime = +moment(minTime).endOf("day").add(maxDaysRange, "days");
    console.log(
        "checkGroupInsertionTimeRange insertion %s min %s max %s (days in range %s)",
        InsertionTime,
        _minTime,
        _maxTime,
        maxDaysRange
    );
    return isBetween(InsertionTime, _minTime, _maxTime);
}

export function checkGroupTimeForLocation(
    locationID,
    porkerTime,
    pigletTime,
    time = +new Date()
) {
    let animals = animalsDB.getAllAnimalsByPlcmntID(locationID, {
        joinEvents: false,
    });
    let group = getGroupForAnimals(animals);
    if (group) {
        const daysToAdd =
            AnimalTypes.PORKER === animals[0].AnimalKind
                ? porkerTime
                : pigletTime;
        return checkGroupInsertionTimeRange(group, time, daysToAdd);
    }
    return true;
}

export function getAnimalsNumber(group) {
    let sum = 0;
    if (!group.AnmIDs.length) return sum;
    for (let id of group.AnmIDs) {
        let animal = animalsDB.getAnimalById(id);
        if (animal) sum += animal.AnmCnt;
    }
    return sum;
}

export function setBirthDate(group, timestamp) {
    group.GroupBirthTime = timestamp;
}

export const isSubgroup = (animal) => {
    const individualTypes = [
        AnimalTypes.SOW,
        AnimalTypes.RENOVATION_SOW,
        AnimalTypes.BOAR,
    ];
    if (individualTypes.includes(animal.AnimalKind)) return false;
    return !!(animal.RFID || animal.Tagged);
};

// const isAnimalEligibleForTimespan = (AnmID) => {
//     const animal = animalsDB.getAnimalById(AnmID, {joinEvents: false});
//     if (!animal) return false;
//     return !isSubgroup(animal);
// };

export async function getGroupAnimalTypes(queryCaller, anmGrp) {
    try {
        const result = await queryCaller(GROUP_ANIMAL_TYPES, [anmGrp]);
        return result[0];
    } catch (e) {
        console.error(e);
        return {hasWeaners: false, hasGilts: false, hasFinisher: false};
    }
}

export function getGroupFatteningTimes(queryCaller, anmGrp) {
    return queryCaller(GROUP_FATTENING, [anmGrp]);
}

export function getGroupMortalityChartData(
    queryCaller,
    anmGrp,
    endTimestamp = null
) {
    try {
        return queryCaller(GroupMortalityQuery, [
            anmGrp,
            anmGrp,
            anmGrp,
            anmGrp,
            anmGrp,
            endTimestamp,
        ]);
    } catch (e) {
        console.error(e);
        return [];
    }
}

export function getLastInsertedGroups(queryCaller, limit = 1) {
    return queryCaller(createLastXInserted(limit)); // alasql nie obsluguje wstawiania ? w limit
}

export function getLastClosedGroups(queryCaller, limit = 1) {
    return queryCaller(createLastXClosed(limit));
}

export function getGroupsSortedByInsertion(queryCaller, yearRange) {
    return queryCaller(groupsSortedByInsertion(yearRange)); // alasql nie obsluguje wstawiania ? w limit
}

export function sortWeightEvents(events) {
    return events.sort((a, b) => {
        // Sortujemy po dokładnym czasie (najstarsze pierwsze)
        if (a.EvTime !== b.EvTime) {
            return a.EvTime - b.EvTime;
        }

        // Przy tym samym czasie sortujemy po EvData.Weight (najmniejsze pierwsze)
        return (a.EvData?.Weight || 0) - (b.EvData?.Weight || 0);
    });
}

function generateTransfersObj(
    transfers,
    buildingsMap = new Map(),
    AnmGrp,
    EvTime
) {
    const tmp = {
        Added: [],
        Removed: [],
        PigletToPorker: [],
    };
    for (let event of transfers) {
        const {
            EvCode,
            EvData: {RemovedReason, DstID, SrcID},
        } = event;
        // loszka dołączona do grupy bez wcześniejszej grupy (OldGroupID === AnmID) -> zwykłe przeniesienie
        const {NewGroupID, OldGroupID, GroupTransfer} =
            getTransferGroupData(event);
        if (
            RemovedReason ||
            (OldGroupID === AnmGrp &&
                (OldGroupID !== NewGroupID || GroupTransfer !== AnmGrp))
        ) {
            tmp.Removed.push(event);
        } else if (
            (EvCode === EventTypes.INSERTION && event.EvTime !== EvTime) ||
            NewGroupID === AnmGrp ||
            (GroupTransfer === AnmGrp && OldGroupID !== AnmGrp) ||
            EvCode === EventTypes.TECHNOLOGY_GROUP_TRANSFER
        ) {
            tmp.Added.push(event);
        } else if (DstID && SrcID) {
            const pigletLoc = buildingsMap.get(SrcID);
            const porkerLoc = buildingsMap.get(DstID);
            if (
                pigletLoc &&
                porkerLoc &&
                pigletLoc.SType === SectorTypes.PIGLET_HOUSE &&
                porkerLoc.SType === SectorTypes.PORK_HOUSE
            ) {
                tmp.PigletToPorker.push(event);
            }
        }
    }
    return tmp;
}

function generateWeightingObj(weightingEvents) {
    const tmp = {
        [EventTypes.WEIGHTING]: [],
        [EventTypes.INSERTION]: [],
        [EventTypes.FALL]: [],
        [EventTypes.SELL]: [],
    };
    for (let event of weightingEvents) {
        const {EvCode} = event;
        if (tmp[EvCode]) {
            tmp[EvCode].push(event);
        } else {
            tmp[EvCode] = [event];
        }
    }
    return tmp;
}

function createBiomas(events, insertions, AnmGrp) {
    let sum = 0;
    for (let ev of events) {
        const EvTime = +moment.utc(ev.EvTime).startOf("d");
        const biomas =
            convertWeightUnitTo(
                ev.EvData.Weight || insertions.estimatedWeight?.[EvTime],
                {unit: UnitTypes.MEDIUM}
            ) * (ev.AnmCnt || 1);
        if (biomas) sum += biomas;
    }
    console.log(
        `[FCR - ${AnmGrp} - Tworzenie biomasy z ev] Suma biomasy:`,
        sum
    );
    console.log(`[FCR - ${AnmGrp} - Tworzenie biomasy z ev] Events:`, events);
    return sum;
}

function fcrWeightFormatter(weight) {
    if (!weight) return null;
    return convertWeightUnitTo(weight, {
        unit: UnitTypes.MEDIUM,
    });
}

export function calculateFCRForClosedGroup(
    group,
    _transfers,
    forageUsage,
    weightingEvents = [],
    insertions = {},
    buildingsMap = {},
    calculatingForPorker = false,
    reclassifyTime,
    pigletToPorkhouse
) {
    const weightingEventsByCode = generateWeightingObj(weightingEvents);
    const insertionEvent =
        weightingEventsByCode[EventTypes.INSERTION][0] ||
        weightingEventsByCode[EventTypes.WEIGHTING][0];
    console.log(
        insertionEvent,
        calculatingForPorker,
        insertions,
        _transfers,
        pigletToPorkhouse,
        "testtt"
    );

    const firstEvent = first(weightingEvents);

    if (calculatingForPorker) {
        console.log(
            "[FCR] Pierwszy event do biomasy początkowej",
            firstEvent,
            insertions?.porkerStart?.Amount,
            weightingEvents
        );
    }

    const biomasStart = !calculatingForPorker
        ? 0
        : insertions?.porkerStart?.Amount *
          fcrWeightFormatter(
              firstEvent?.EvData?.Weight || insertions?.porkerStart?.Weight
          );

    const transfers = generateTransfersObj(
        _transfers,
        buildingsMap,
        group.AnmGrp,
        insertionEvent?.EvTime
    );

    const lastEventWeight = fcrWeightFormatter(
        insertions?.estimatedWeightIndex?.getClosest(reclassifyTime)
    );

    const lastEventBiomas =
        !calculatingForPorker && reclassifyTime && pigletToPorkhouse?.length
            ? pigletToPorkhouse.reduce((acc, item) => {
                  const val = Number(item?.AnmCnt) || 0;
                  return acc + val;
              }, 0) * lastEventWeight
            : 0;

    const biomasIn = createBiomas(
        transfers.Added || [],
        insertions,
        group.AnmGrp
    );
    const biomasOut = createBiomas(
        transfers.Removed || [],
        insertions,
        group.AnmGrp
    );

    const biomasFall = createBiomas(
        weightingEventsByCode[EventTypes.FALL] || [],
        insertions,
        group.AnmGrp
    );
    const biomasSell = createBiomas(
        weightingEventsByCode[EventTypes.SELL] || [],
        insertions,
        group.AnmGrp
    );

    const biomasUsage =
        biomasSell +
        biomasFall +
        biomasOut +
        lastEventBiomas -
        (biomasStart + biomasIn);
    const animallog = calculatingForPorker ? "Tucznik" : "Warchlak";
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Przeniesienia:`,
        transfers,
        _transfers
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Ostatni event biomas:`,
        lastEventBiomas,
        pigletToPorkhouse,
        lastEventWeight
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Biomas start: ${biomasStart}`
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Biomas transferów (In): ${biomasIn}`
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Biomas transferów (Out): ${biomasOut}`
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Biomas upadków (Fall): ${biomasFall}`
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Biomas sprzedaży (Sell): ${biomasSell}`
    );

    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Obiekt oczekiwanej wagi i ilość zwierząt:`,
        insertions
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Obiekt zdarzeń ważenia:`,
        weightingEventsByCode
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Zużycie biomasy (przyrost): ${biomasUsage}`
    );
    console.log(
        `[FCR - ${group.AnmGrp}] ${animallog} (ZAMKNIĘTA) Zużycie paszy (forageUsage): ${forageUsage}`
    );
    return forageUsage / biomasUsage;
}
